import { ReactElement, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { FormProvider, useForm } from 'react-hook-form';
import Button from '../controls/Button';
import CustomTextArea from '../controls/CustomTextArea';
import CustomInputBox from '../controls/CustomInputBox';
import { requestAccountDeletion } from '../services/dashboard-service';

/**
 * Form data interface for account deletion request.
 */
interface DeletionRequestFormData {
  email: string;
  reason: string;
  confirmationText: string;
}

/**
 * Request Account Deletion page component.
 * Allows users to request account deletion with reason and confirmation.
 * @returns A React element representing the Request Deletion page.
 */
export default function RequestDeletionPage(): ReactElement {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);

  const methods = useForm<DeletionRequestFormData>({
    mode: 'all',
    defaultValues: {
      email: '',
      reason: '',
      confirmationText: ''
    }
  });

  const { handleSubmit, watch } = methods;
  const confirmationText = watch('confirmationText');
  const email = watch('email');

  /**
   * Handles the form submission for account deletion request.
   * @param data - The form data containing email, reason, and confirmation text.
   */
  const handleDeletionRequest = async (data: DeletionRequestFormData): Promise<void> => {
    try {
      setLoading(true);

      // Validate confirmation text
      if (data.confirmationText.toLowerCase() !== 'delete my account') {
        toast.error('Please type "delete my account" to confirm');
        setLoading(false);
        return;
      }

      // Here you would typically send the deletion request to your backend API
      // For now, we'll simulate the request
      await requestAccountDeletion(data);

      toast.success(
        'Account deletion request submitted successfully. We will process your request within 24-48 hours.'
      );

      // Redirect to login page or home page
      navigate('/auth/login');
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Error submitting deletion request:', error);
      toast.error('Failed to submit deletion request. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  /**
   * Handles the cancel action by navigating back to the previous page.
   */
  const handleCancel = (): void => {
    navigate(-1); // Go back to previous page
  };

  return (
    <div className="my-6 mx-auto p-6 flex flex-1 flex-col bg-container-500 rounded max-w-2xl">
      <div className="flex flex-1 flex-col">
        {/* Header */}
        <div className="text-white text-2xl font-semibold mb-2">Request Account Deletion</div>
        <div className="text-gray-400 text-sm mb-8">
          We&apos;re sorry to see you go. Please provide the following information to request
          account deletion.
        </div>

        {/* Warning Notice */}
        <div className="bg-error-900/20 border border-error-500/30 rounded-lg p-4 mb-6">
          <div className="flex items-start">
            <div className="material-icons-outlined text-error-400 mr-3 mt-0.5">warning</div>
            <div className="text-error-200 text-sm">
              <div className="font-semibold mb-2">Important Notice:</div>
              <ul className="list-disc list-inside space-y-1">
                <li>Account deletion is permanent and cannot be undone</li>
                <li>
                  All your data, including device configurations, historical data, and settings will
                  be permanently deleted
                </li>
                <li>This process may take 24-48 hours to complete</li>
                <li>You will receive a confirmation email once the deletion is processed</li>
              </ul>
            </div>
          </div>
        </div>

        {/* Form */}
        {/* eslint-disable-next-line react/jsx-props-no-spreading */}
        <FormProvider {...methods}>
          <form className="w-full" onSubmit={handleSubmit(handleDeletionRequest)}>
            <div className="space-y-6">
              {/* Email Confirmation */}
              <div>
                {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
                <label htmlFor="email" className="block text-sm font-medium text-gray-300 mb-2">
                  Confirm your email address
                </label>
                <CustomInputBox
                  idname="email"
                  type="email"
                  placeholder="Enter your email address"
                  autoComplete="email"
                  validationObject={{
                    required: 'Email is required',
                    pattern: {
                      value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                      message: 'Please enter a valid email address'
                    }
                  }}
                  noMarginTop
                />
              </div>

              {/* Reason for Deletion */}
              <div>
                {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
                <label htmlFor="reason" className="block text-sm font-medium text-gray-300 mb-2">
                  Reason for account deletion (optional)
                </label>
                <CustomTextArea idname="reason" noMarginTop />
              </div>

              {/* Confirmation Text */}
              <div>
                {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
                <label
                  htmlFor="confirmationText"
                  className="block text-sm font-medium text-gray-300 mb-2"
                >
                  Type &quot;delete my account&quot; to confirm
                </label>
                <CustomInputBox
                  idname="confirmationText"
                  type="text"
                  placeholder="Type: delete my account"
                  autoComplete="off"
                  validationObject={{
                    required: 'Confirmation text is required',
                    /**
                     * Validates that the user has typed the exact confirmation text.
                     * @param value - The input value to validate.
                     * @returns True if valid, error message if invalid.
                     */
                    validate: (value: string) => {
                      return (
                        value.toLowerCase() === 'delete my account' ||
                        'Please type "delete my account" exactly'
                      );
                    }
                  }}
                  noMarginTop
                />
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex justify-end gap-4 mt-8 pt-6 border-t border-gray-700">
              <Button
                buttonType="button"
                theme="secondary-gray"
                size="md"
                name="Cancel"
                onClick={handleCancel}
              />
              <Button
                buttonType="submit"
                theme="danger"
                size="md"
                name="Request Account Deletion"
                loading={loading}
                disabled={!email || confirmationText.toLowerCase() !== 'delete my account'}
              />
            </div>
          </form>
        </FormProvider>

        {/* Additional Information */}
        <div className="mt-8 pt-6 border-t border-gray-700">
          <div className="text-gray-400 text-sm">
            <div className="font-medium mb-2">Need help or have questions?</div>
            <div className="space-y-1">
              <div>• Contact our support team at support@telosair.com</div>
              <div>• Visit our help center for more information</div>
              <div>• Consider temporarily disabling your account instead of permanent deletion</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
